/**
 * Toast Atom
 * @layer atom
 * @package @plerity/ui-core
 */

import { clsx } from "clsx";
import { useEffect } from "react";
import type { ToastContainerProps, ToastPosition, ToastProps } from "./Toast.types";

// Literal color-scale accents (not the web-design `--success`/`--danger` token
// namespace, which is undefined in every React SPA app's token layer — only
// portal defines it). These mirror Card's accent border vocabulary and render
// identically in light + dark.
const borderColors: Record<string, string> = {
  success: "border-l-green-500 dark:border-l-green-400",
  error: "border-l-red-500 dark:border-l-red-400",
  warning: "border-l-amber-500 dark:border-l-amber-400",
  info: "border-l-brand-500 dark:border-l-brand-400",
};

const iconPaths: Record<string, string> = {
  success: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  error: "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z",
  warning:
    "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
  info: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
};

const iconColors: Record<string, string> = {
  success: "text-success-strong",
  error: "text-danger-strong",
  warning: "text-warning-strong",
  info: "text-brand-600 dark:text-brand-400",
};

/**
 * ARIA role selection:
 * - "alert" (implicit aria-live="assertive") for error/warning — urgent, must interrupt.
 * - "status" (implicit aria-live="polite") for success/info — non-urgent, waits for pause.
 */
function getAriaProps(variant: string): { role: "alert" | "status"; "aria-live": "assertive" | "polite" } {
  if (variant === "error" || variant === "warning") {
    return { role: "alert", "aria-live": "assertive" };
  }
  return { role: "status", "aria-live": "polite" };
}

export function Toast({ toast, onDismiss }: ToastProps) {
  const { id, variant, title, description, duration = 4000, dismissible = true, action } = toast;

  useEffect(() => {
    if (duration <= 0) return;
    const timer = setTimeout(() => onDismiss(id), duration);
    return () => clearTimeout(timer);
  }, [id, duration, onDismiss]);

  const ariaProps = getAriaProps(variant);

  return (
    <div
      {...ariaProps}
      className={clsx(
        "w-[360px] max-w-full",
        // Literal white/gray-800 surface — do NOT use `bg-surface` or
        // `border-[var(--color-border-default)]`. Those emit `var(--color-*)` with
        // NO fallback and are stripped from the React SPA apps' Tailwind v4 build.
        "bg-white border border-gray-200 border-l-[3px] dark:bg-gray-800 dark:border-gray-700",
        "rounded-md shadow-lg",
        "p-4",
        "animate-[slideInRight_200ms_ease]",
        borderColors[variant]
      )}
    >
      <div className="flex items-start gap-3">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={clsx("shrink-0 mt-0.5", iconColors[variant])}
          aria-hidden="true"
        >
          <path d={iconPaths[variant]} />
        </svg>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</p>
          {description && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>}
          {action && (
            <button
              type="button"
              onClick={() => {
                action.onPress();
                onDismiss(id);
              }}
              className="mt-2 rounded text-sm font-medium text-brand-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 dark:text-brand-400"
            >
              {action.label}
            </button>
          )}
        </div>
        {dismissible && (
          <button
            type="button"
            onClick={() => onDismiss(id)}
            className="shrink-0 rounded p-0.5 text-gray-400 transition-colors duration-150 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gray-400 dark:hover:text-gray-100"
            aria-label="Dismiss"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

const positionClasses: Record<ToastPosition, string> = {
  "top-right": "fixed top-6 right-6 z-[300] flex flex-col gap-3",
  "bottom-right": "fixed bottom-6 right-6 z-[300] flex flex-col gap-3",
};

export function ToastContainer({ toasts, onDismiss, position = "bottom-right" }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div
      className={positionClasses[position]}
      /**
       * No aria-live here — individual Toast items carry role="alert" or role="status".
       * Nesting aria-live="polite" around role="alert" items causes double announcements
       * in VoiceOver/NVDA. Each toast self-announces when injected into the DOM.
       */
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
